
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Package, History, Info } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import StockAdjustment from './StockAdjustment';
import InventoryHistory from './InventoryHistory';

interface ProductInventoryProps {
  id?: string;
  name?: string;
  quantity: number;
  setQuantity: (value: number) => void;
  lowStockThreshold: number;
  setLowStockThreshold: (value: number) => void;
  isOutOfStock: boolean;
  setIsOutOfStock: (value: boolean) => void;
  sku: string;
  setSku: (value: string) => void;
}

const ProductInventory = ({
  id,
  name = 'Product',
  quantity,
  setQuantity,
  lowStockThreshold,
  setLowStockThreshold,
  isOutOfStock,
  setIsOutOfStock,
  sku,
  setSku
}: ProductInventoryProps) => {
  const [activeTab, setActiveTab] = useState('details');

  const handleStockUpdated = (newQuantity: number) => {
    setQuantity(newQuantity);
    setIsOutOfStock(newQuantity <= 0);
  };

  const getStockStatusClass = () => {
    if (quantity <= 0) return 'text-red-600 dark:text-red-400';
    if (quantity <= lowStockThreshold) return 'text-amber-600 dark:text-amber-400';
    return 'text-green-600 dark:text-green-400';
  };

  const getStockStatusText = () => {
    if (quantity <= 0) return 'Out of stock';
    if (quantity <= lowStockThreshold) return 'Low stock';
    return 'In stock';
  };

  return (
    <Card className="bg-white dark:bg-[#1e1e2d] border-gray-200 dark:border-gray-800">
      <CardHeader className="pb-3">
        <CardTitle className="text-md flex items-center gap-2 dark:text-gray-200">
          <Package className="h-4 w-4" />
          Inventory
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4 dark:bg-[#151521]">
            <TabsTrigger 
              value="details" 
              className="flex items-center gap-1 dark:text-gray-300 dark:data-[state=active]:bg-[#2d2d3d] dark:data-[state=active]:text-[#a78bfa]"
            >
              <Info className="h-4 w-4" />
              Details
            </TabsTrigger>
            {id && (
              <TabsTrigger 
                value="history" 
                className="flex items-center gap-1 dark:text-gray-300 dark:data-[state=active]:bg-[#2d2d3d] dark:data-[state=active]:text-[#a78bfa]"
              >
                <History className="h-4 w-4" />
                History
              </TabsTrigger>
            )}
          </TabsList>
          
          <TabsContent value="details">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sku" className="form-label dark:text-gray-300">SKU (Stock Keeping Unit)</Label>
                  <Input
                    id="sku"
                    placeholder="Product SKU"
                    value={sku}
                    onChange={(e) => setSku(e.target.value)}
                    className="dark:bg-[#151521] dark:border-gray-700 dark:text-gray-200"
                  />
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="quantity" className="form-label dark:text-gray-300">Quantity</Label>
                    <span className={`text-sm font-medium ${getStockStatusClass()}`}>
                      {getStockStatusText()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      id="quantity"
                      type="number"
                      placeholder="0"
                      value={quantity}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        setQuantity(value);
                        setIsOutOfStock(value <= 0);
                      }}
                      min="0"
                      className="dark:bg-[#151521] dark:border-gray-700 dark:text-gray-200"
                    />
                    {id && (
                      <StockAdjustment
                        productId={id}
                        productName={name}
                        currentStock={quantity}
                        onStockUpdated={handleStockUpdated}
                      />
                    )}
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="lowStockThreshold" className="form-label dark:text-gray-300">Low Stock Threshold</Label>
                <Input
                  id="lowStockThreshold"
                  type="number"
                  placeholder="5"
                  value={lowStockThreshold}
                  onChange={(e) => setLowStockThreshold(parseInt(e.target.value) || 0)}
                  min="0"
                  className="dark:bg-[#151521] dark:border-gray-700 dark:text-gray-200"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  You'll be notified when stock falls below this number
                </p>
              </div>
            </div>
          </TabsContent>
          
          {id && (
            <TabsContent value="history">
              <InventoryHistory productId={id} />
            </TabsContent>
          )}
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default ProductInventory;
